import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import JSZip from "jszip"; // For ZIP extraction
import addLogDocument from "../addLogDocument"; // Custom log function
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken"; // Token utility
import { Box } from "@mui/material";
import Spinner from "../../loading/Spinner";
import SmallSpinner from "../../loading/SmallSpinner";
import Icon1 from "../../icons/Icon";
import { useSnackbar } from "notistack";
import { translateDocumentId } from "../../pages/folder_pages/FolderV2";

import { addActivityLog } from "../../config/OnlineStatus.js";
const UploadXml2 = ({ folder, onUpdate, prefix, modif_thermal_xml }) => {
  const [files, setFiles] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      try {
        setLoading(true);
        const token = await getOrRefreshFirebaseToken(); // Get or refresh token
        const auth = getAuthCentral();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          console.error("User not authenticated");
          setLoading(false);
          return;
        }

        const db = getFirestore();
        const userRef = doc(db, "user", currentUser.uid);
        const userSnapshot = await getDoc(checkDB(userRef));

        if (!userSnapshot.exists()) {
          console.error("User document not found");
          setLoading(false);
          return;
        }

        const org_id = userSnapshot.get("org_id");
        let repo_name = await translateDocumentId("xml");
        await addActivityLog(
          "Documents déposés " + repo_name,
          folder.ref,
          org_id,
          userRef.id,
          "file_drop",
          prefix
        );

        const newFiles = [...files, ...acceptedFiles];
        setFiles(newFiles);

        for (const file of acceptedFiles) {
          if (!file.name.endsWith(".zip") && !file.name.endsWith(".xml")) {
            console.warn("File is not a ZIP:", file.name);
            handleClickVariant(
              "Le fichier n'est pas un fichier .zip ou .xml",
              "error"
            );
            return;
          }
        }

        for (const file of acceptedFiles) {
          let fileBytes = await file.arrayBuffer(); // Read file as bytes
          let extractedXml = null;
          if (false) {
            extractedXml = await extractXmlFromZip(fileBytes);
            if (!extractedXml) {
              console.warn("No XML found in ZIP file:", file.name);
              continue; // Skip this file if no XML found
            }
          }

          const fileData = extractedXml || fileBytes; // Use extracted XML or original file
          const fileName = extractedXml ? `extracted-${file.name}` : file.name;
          const url = modif_thermal_xml
            ? `${prefix}folders/${folder.id}/modif-thermal-xml`
            : `${prefix}folders/${folder.id}/xml`;
          const formData = new FormData();
          formData.append("file", new Blob([fileData]), fileName);
          let start = new Date();

          // Send request
          const response = await fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });
          let end = new Date();
          let elapsed = end - start;
          const responseText = await response.text();
          await addLogDocument(
            url,
            prefix,
            userRef,
            org_id,
            response.status.toString(),
            folder.id,
            "",
            "", // request body as string
            responseText,
            "POST",
            elapsed
          );

          if (!response.ok) {
            console.error(
              `Error uploading file ${file.name}: ${response.status}`
            );
          }
        }

        onUpdate(); // Trigger parent callback
      } catch (error) {
        console.error("Error uploading files:", error);
      } finally {
        setLoading(false);
      }
    },
    [files, folder, onUpdate, prefix]
  );

  // Extract XML from a ZIP file
  const extractXmlFromZip = async (zipBytes) => {
    try {
      const zip = await JSZip.loadAsync(zipBytes);
      for (const filename in zip.files) {
        if (filename.endsWith(".xml")) {
          return await zip.file(filename).async("string"); // Return the first XML as a string
        }
      }
      return null; // Return null if no XML is found
    } catch (error) {
      console.error("Error extracting XML from ZIP:", error);
      return null;
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    accept: {
      "application/pdf": [],
      "application/xml": [],
      "application/zip": [],
      "application/x-zip-compressed": [],
      "text/xml": [],
    },
    maxFiles: Infinity,
    maxSize: 52428800,
  });

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Box
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          border: "1px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: "8px",
          height: "30px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0px 0px",
          width: "30px",
          cursor: "pointer",
          backgroundColor: loading
            ? "rgba(0, 0, 0, 0.1)"
            : isDragActive
              ? "rgba(0, 0, 255, 0.1)"
              : isHovered
                ? "rgba(0, 0, 0, 0.03)"
                : "transparent",
        }}
      >
        <input {...getInputProps()} />
        {loading ? (
          <SmallSpinner loading={true} />
        ) : (
          <Icon1 icon_name="join" size={15} fill="grey" />
        )}
      </Box>
    </div>
  );
};

export default UploadXml2;
